import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { TEntityType } from '@/components/entity-detail/types/EntityType.types';

interface CurrentCustomer {
    id: string;
    name: string;
    imageUrl: string;
}

interface CurrentTicket {
    id: string;
    name: string;
}

interface CurrentTask {
    id: string;
    name: string;
}

interface CurrentDevice {
    id: string;
    name: string;
    imageUrl: string;
}

interface CurrentEmployee {
    id: string;
    name: string;
    imageUrl: string;
}

export type CurrentSection =
    | 'device'
    | 'ticket'
    | 'task'
    | 'customer'
    | 'employee';

export interface CoordinationState {
    customer: {
        current: CurrentCustomer | null;
        params: any;
        dialogType: TEntityType;
    };
    device: {
        current: CurrentDevice | null;
        params: any;
        dialogType: TEntityType;
    };
    task: {
        taskMode: 'view' | 'edit';
        current: CurrentTask | null;
        newTask: any;
        params: any;
        dialogType: TEntityType;
    };
    ticket: {
        taskMode: 'view' | 'edit';
        current: CurrentTicket | null;
        newTicket: any;
        params: any;
        dialogType: TEntityType;
    };
    employee: {
        current: CurrentEmployee | null;
        params: any;
        dialogType: TEntityType;
    };
    currentSection: CurrentSection;
}

const initialState: CoordinationState = {
    customer: {
        current: null,
        params: {},
        dialogType: 'CUSTOMER',
    },
    device: {
        current: null,
        params: {},
        dialogType: 'DEVICE',
    },
    ticket: {
        taskMode: 'view',
        current: null,
        newTicket: null,
        params: {},
        dialogType: 'TICKET',
    },
    task: {
        taskMode: 'view',
        current: null,
        newTask: null,
        params: {},
        dialogType: 'TASK',
    },
    employee: {
        current: null,
        params: {},
        dialogType: 'ASSIGNEE',
    },
    currentSection: 'customer',
};

export const coordinationSlice = createSlice({
    name: 'simpleCoordination',
    initialState,
    reducers: {
        setCurrentSectionAction: (
            state,
            action: PayloadAction<CoordinationState['currentSection']>
        ) => {
            state.currentSection = action.payload;
        },

        setCurrentCustomerAction: (
            state,
            action: PayloadAction<CoordinationState['customer']['current']>
        ) => {
            state.customer.current = action.payload;
        },

        setParamCustomerAction: (
            state,
            action: PayloadAction<CoordinationState['customer']['params']>
        ) => {
            state.customer.params = action.payload;
        },

        setCurrentDeviceAction: (
            state,
            action: PayloadAction<CoordinationState['device']['current']>
        ) => {
            state.device.current = action.payload;
        },

        setParamDeviceAction: (
            state,
            action: PayloadAction<CoordinationState['device']['params']>
        ) => {
            state.device.params = action.payload;
        },

        setCurrentTicketAction: (
            state,
            action: PayloadAction<CoordinationState['ticket']['current']>
        ) => {
            state.ticket.current = action.payload;
        },

        setParamTicketAction: (
            state,
            action: PayloadAction<CoordinationState['ticket']['params']>
        ) => {
            state.ticket.params = action.payload;
        },

        setCurrentTaskAction: (
            state,
            action: PayloadAction<CoordinationState['task']['current']>
        ) => {
            state.task.current = action.payload;
        },

        setParamTaskAction: (
            state,
            action: PayloadAction<CoordinationState['task']['params']>
        ) => {
            state.task.params = action.payload;
        },

        setNewTicket: (
            state,
            action: PayloadAction<CoordinationState['ticket']['newTicket']>
        ) => {
            state.ticket.newTicket = action.payload;
        },

        setNewTask: (
            state,
            action: PayloadAction<CoordinationState['task']['newTask']>
        ) => {
            state.task.newTask = action.payload;
        },

        setParamsAction: (
            state,
            action: PayloadAction<{
                key: CurrentSection;
                params: Record<string, any>;
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].params = {
                    ...state[action.payload.key].params,
                    ...action.payload.params,
                };
            }
        },

        setSelectedModelAction: (
            state,
            action: PayloadAction<{
                key: CurrentSection;
                data:
                    | CurrentCustomer
                    | CurrentDevice
                    | CurrentTask
                    | CurrentTicket
                    | CurrentEmployee;
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].current = action.payload.data;
            }
        },

        setClearModelAction: (
            state,
            action: PayloadAction<{
                key: CurrentSection;
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].current = null;
            }
        },

        setClearParamsAndCurrentAction: (
            state,
            action: PayloadAction<{
                excludeKey: CurrentSection;
                nextKeys: CurrentSection[];
                prevKeys: CurrentSection[];
            }>
        ) => {
            const length = action.payload.nextKeys.length - 1;

            let paramsKey = [] as CurrentSection[];

            // FIND LAST KEY TO GET ALL PARAM BELONG TO 'excludeParamKey'
            for (let i = length; i >= 0; i--) {
                const data = state[action.payload.nextKeys[i]];

                if (Object.keys(data.params).length > 0) {
                    paramsKey = Object.keys(data.params).map(
                        paramKey => paramKey.split('Id')[0]
                    ) as CurrentSection[];

                    break;
                }
            }

            const nextKeys = action.payload.nextKeys;

            for (const key of nextKeys) {
                state[key].current = null;

                // DELETE ALL PARAM BELONG TO KEY, KEEP PREV KEYS
                paramsKey.forEach(paramKey => {
                    if (
                        state[key].params[`${paramKey}Id`] &&
                        !action.payload.prevKeys.includes(paramKey)
                    ) {
                        delete state[key].params[`${paramKey}Id`];
                    }
                });
            }
        },
    },
});

export const {
    setClearParamsAndCurrentAction,
    setClearModelAction,
    setSelectedModelAction,
    setParamsAction,
    setCurrentDeviceAction,
    setParamDeviceAction,
    setParamCustomerAction,
    setParamTicketAction,
    setParamTaskAction,
    setCurrentSectionAction,
    setCurrentCustomerAction,
    setCurrentTicketAction,
    setCurrentTaskAction,
    setNewTicket,
    setNewTask,
} = coordinationSlice.actions;

export default coordinationSlice.reducer;
